import { SHAPE_POINTS_BIN_RECORD_BYTES } from "./compactConstants";

/**
 * Normalizes wire `points_bin` after MessagePack decode into a `Uint8Array` for {@link CompactShape.fromBuffer}.
 *
 * Only binary types are accepted (`Uint8Array`, `ArrayBuffer`, typed array views). The API must be called
 * with `Accept: application/msgpack` so `points_bin` decodes as MessagePack `bin`.
 *
 * @param raw Decoded `points_bin` field from {@link ShapeWire}.
 * @param context Human-readable source (e.g. `Snapshot shape <id>`) for error messages.
 * @returns Bytes whose length is a multiple of {@link SHAPE_POINTS_BIN_RECORD_BYTES}.
 * @throws {Error} When the value is missing, not binary, legacy `points[]`, or wrong byte length.
 */
export function coercePointsBin( raw: unknown, context: string ): Uint8Array {
    if ( raw instanceof Uint8Array ) {
        if ( raw.byteLength % SHAPE_POINTS_BIN_RECORD_BYTES !== 0 ) {
            throw new Error( `${ context }: points_bin length ${ raw.byteLength } is not a multiple of ${ SHAPE_POINTS_BIN_RECORD_BYTES }` );
        }

        return raw;
    }

    if ( raw instanceof ArrayBuffer ) {
        return coercePointsBin( new Uint8Array( raw ), context );
    }

    if ( ArrayBuffer.isView( raw ) ) {
        return coercePointsBin(
            new Uint8Array( raw.buffer, raw.byteOffset, raw.byteLength ),
            context,
        );
    }

    if ( Array.isArray( raw ) ) {
        const first: unknown = raw[0];
        if ( raw.length > 0 && typeof first === "object" && first !== null ) {
            throw new Error( `${ context }: legacy shape "points" array is not supported; use points_bin with MessagePack.` );
        }

        throw new Error( `${ context }: points_bin must be MessagePack bin (Uint8Array), not a JSON byte array; ` +
          "send Accept: application/msgpack." );
    }

    if ( raw === undefined || raw === null ) {
        throw new Error( `${ context }: points_bin is missing` );
    }

    throw new Error( `${ context }: points_bin must be MessagePack bin (Uint8Array), got ${ typeof raw }` );
}
